import React, { useState, useEffect } from 'react';
import { 
  Target, Calendar as CalendarIcon, Clock, Plus, ChevronLeft, ChevronRight,
  BarChart3, CheckCircle2, AlertCircle, Pause, Play, Edit2, Trash2, Eye,
  TrendingUp, Users, DollarSign, Megaphone, FileText, Video, Globe, 
  MessageSquare, Package, Zap, Filter, LayoutGrid, GanttChart, X, Check,
  Layers, ArrowLeft, Rocket
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { CampaignBlueprintWizard } from '@/components/shared/CampaignBlueprint';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section Header Component - STANDARDIZED
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-5 py-3 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
            <Icon size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white font-heading">{title}</h3>
            <p className="text-[10px] text-[hsl(var(--text-muted))]">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
  );
}

// Campaign type configurations
const campaignTypes = {
  challenge: { label: 'Challenge/Contest', icon: Target, color: '#E63946' },
  webinar: { label: 'Webinar/Seminar', icon: Video, color: '#3B82F6' },
  bootcamp: { label: 'Bootcamp/Class', icon: Users, color: '#10B981' },
  promo: { label: 'Promo/Offer', icon: Package, color: '#F59E0B' },
  grow: { label: 'Grow Together', icon: TrendingUp, color: '#8B5CF6' },
  product: { label: 'Product Launching', icon: Rocket, color: '#EC4899' },
  other: { label: 'Other', icon: Megaphone, color: '#6B7280' },
};

// Status configurations - Campaign Lifecycle Phases
const statusConfig = {
  // Planning & Preparation Phase
  planning: { label: 'Planning', color: '#6B7280', icon: FileText, phase: 1 },
  preparation: { label: 'Preparation', color: '#F59E0B', icon: Clock, phase: 2 },
  // Pre-Launch Phase
  prospecting: { label: 'Prospecting', color: '#8B5CF6', icon: Users, phase: 3 },
  seeding: { label: 'Seeding', color: '#06B6D4', icon: MessageSquare, phase: 4 },
  // Launch Phase
  buzz: { label: 'Buzz', color: '#EC4899', icon: Zap, phase: 5 },
  launching: { label: 'Launching', color: '#F97316', icon: Megaphone, phase: 6 },
  running: { label: 'Running', color: '#10B981', icon: Play, phase: 7 },
  // Closing Phase
  closing: { label: 'Closing', color: '#14B8A6', icon: Target, phase: 8 },
  completed: { label: 'Completed', color: '#3B82F6', icon: CheckCircle2, phase: 9 },
  kiv: { label: 'KIV', color: '#E63946', icon: Pause, phase: 0 },
};

// Preparation checklist items
const preparationChecklist = [
  { id: 'funnel', label: 'Funnel' },
  { id: 'budget', label: 'Budget' },
  { id: 'goals', label: 'Goals' },
  { id: 'package', label: 'Package Offer' },
  { id: 'script', label: 'Script Content' },
  { id: 'landing', label: 'Landing Page' },
  { id: 'ads', label: 'Ads Creative' },
  { id: 'testimoni', label: 'Testimoni/Feedback' },
];

// Month names
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Get today's date info for current date indicators
const today = new Date();
const todayDay = today.getDate();
const todayMonth = today.getMonth();
const todayYear = today.getFullYear();

export default function ChannelCampaignContent({ ib }) {
  const [viewMode, setViewMode] = useState('timeline'); // timeline or calendar
  const [currentYear, setCurrentYear] = useState(2026);
  const [currentMonth, setCurrentMonth] = useState(0); // For calendar view
  const [selectedTimelineQuarter, setSelectedTimelineQuarter] = useState(null); // For timeline drill-down to quarter (0=Q1, 1=Q2, 2=Q3, 3=Q4)
  const [selectedTimelineMonth, setSelectedTimelineMonth] = useState(null); // For timeline drill-down to weekly
  const [selectedTimelineWeek, setSelectedTimelineWeek] = useState(null); // For timeline drill-down to daily
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const [blueprintDefaultView, setBlueprintDefaultView] = useState('edit'); // 'edit' or 'document'
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [blueprintStep, setBlueprintStep] = useState(1); // For wizard steps
  
  // Load campaigns from MongoDB
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoaded, setCampaignsLoaded] = useState(false);
  const [editMeta, setEditMeta] = useState({});
  
  useEffect(() => {
    if (!ib?.id) return;
    setCampaignsLoaded(false);
    setCampaigns([]);
    const fetchCampaigns = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/ib-data/${ib.id}/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.campaigns) {
          setCampaigns(res.data.data.campaigns);
        }
        if (res.data?.updated_by_name) {
          setEditMeta({ name: res.data.updated_by_name, time: res.data.updated_at });
        }
        setCampaignsLoaded(true);
      } catch (e) {
        console.error('Failed to load campaigns:', e);
      }
    };
    fetchCampaigns();
  }, [ib?.id]);
  
  // Explicit save — only called after user actions (create/edit/delete)
  const saveCampaigns = React.useCallback((data) => {
    if (!ib?.id) return;
    const token = localStorage.getItem('token');
    axios.put(`${API}/ib-data/${ib.id}/campaigns`, { data: { campaigns: data } }, { headers: { Authorization: `Bearer ${token}` } }).catch((err) => console.error('Failed to save campaigns:', err));
  }, [ib?.id]);
  
  // New campaign form state
  const [newCampaign, setNewCampaign] = useState({
    name: '',
    type: 'challenge',
    startDate: '',
    endDate: '',
    status: 'planning',
    description: '',
    preparation: { funnel: false, budget: false, goals: false, package: false, script: false, landing: false, ads: false, testimoni: false },
    metrics: [],
    subCampaigns: [],
    channel: 'public',
  });
  
  // Filter campaigns
  const filteredCampaigns = campaigns.filter(c => {
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    if (filterType !== 'all' && c.type !== filterType) return false;
    return true;
  });
  
  // Get campaigns for specific year
  const yearCampaigns = filteredCampaigns.filter(c => {
    const startYear = new Date(c.startDate).getFullYear();
    const endYear = new Date(c.endDate).getFullYear();
    return startYear === currentYear || endYear === currentYear;
  });
  
  // Stats
  const totalCampaigns = campaigns.filter(c => new Date(c.startDate).getFullYear() === currentYear).length;
  const totalSubCampaigns = campaigns.filter(c => new Date(c.startDate).getFullYear() === currentYear)
    .reduce((sum, c) => sum + (c.subCampaigns?.length || 0), 0);
  const runningCampaigns = campaigns.filter(c => c.status === 'running').length;
  const completedCampaigns = campaigns.filter(c => c.status === 'completed' && new Date(c.startDate).getFullYear() === currentYear).length;
  
  // CRUD handlers — explicit save after each action
  const handleCreateCampaign = () => {
    if (!newCampaign.name || !newCampaign.startDate || !newCampaign.endDate) return;
    const campaign = { ...newCampaign, id: Date.now() };
    const newList = [...campaigns, campaign];
    setCampaigns(newList);
    saveCampaigns(newList);
    setShowCreateModal(false);
    setNewCampaign({ name: '', type: 'challenge', startDate: '', endDate: '', status: 'planning', description: '', preparation: { funnel: false, budget: false, goals: false, package: false, script: false, landing: false, ads: false, testimoni: false }, metrics: [], subCampaigns: [], channel: 'public' });
  };
  
  const handleEditCampaign = () => {
    if (!editingCampaign) return;
    const newList = campaigns.map(c => c.id === editingCampaign.id ? editingCampaign : c);
    setCampaigns(newList);
    saveCampaigns(newList);
    setShowEditModal(false);
    setEditingCampaign(null);
  };
  
  const handleDeleteCampaign = (id) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      const newList = campaigns.filter(c => c.id !== id);
      setCampaigns(newList);
      saveCampaigns(newList);
      setShowDetailModal(false);
    }
  };
  
  const openDetailModal = (campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailModal(true);
  };
  
  const openEditModal = (campaign) => {
    setEditingCampaign({ ...campaign });
    setShowEditModal(true);
    setShowDetailModal(false);
  };
  
  // Calculate campaign position in timeline
  const getCampaignPosition = (campaign) => {
    const startDate = new Date(campaign.startDate);
    const endDate = new Date(campaign.endDate);
    const startMonth = startDate.getMonth();
    const endMonth = endDate.getMonth();
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const daysInStartMonth = new Date(startDate.getFullYear(), startMonth + 1, 0).getDate();
    const daysInEndMonth = new Date(endDate.getFullYear(), endMonth + 1, 0).getDate();
    
    // Calculate left position (percentage within the year)
    const left = ((startMonth * 30 + startDay) / 365) * 100;
    
    // Calculate width
    const startPos = startMonth + (startDay / daysInStartMonth);
    const endPos = endMonth + (endDay / daysInEndMonth);
    const width = ((endPos - startPos + 0.1) / 12) * 100;
    
    return { left: `${(startMonth / 12) * 100}%`, width: `${Math.max(width, 2)}%`, startMonth, endMonth };
  };
  
  // Preparation progress
  const getPreparationProgress = (prep) => {
    if (!prep || typeof prep !== 'object') {
      return { completed: 0, total: 0, percentage: 0 };
    }
    const total = Object.keys(prep).length;
    const completed = Object.values(prep).filter(v => v).length;
    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  return (
    <>
      {/* Sticky Header */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Channel Campaign</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">Campaign Planning & Performance Tracking</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-0.5 sm:p-1">
              <Button
                size="sm"
                variant={viewMode === 'timeline' ? 'default' : 'ghost'}
                className={`h-7 sm:h-8 px-2 sm:px-3 text-xs ${viewMode === 'timeline' ? 'bg-[#E63946] text-white' : 'text-[#9CA3AF]'}`}
                onClick={() => setViewMode('timeline')}
              >
                <GanttChart size={12} className="mr-1" />
                Timeline
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                className={`h-7 sm:h-8 px-2 sm:px-3 text-xs ${viewMode === 'calendar' ? 'bg-[#E63946] text-white' : 'text-[#9CA3AF]'}`}
                onClick={() => setViewMode('calendar')}
              >
                <CalendarIcon size={12} className="mr-1" />
                Calendar
              </Button>
            </div>
            
            <Button
              size="sm"
              onClick={() => { setSelectedCampaign(null); setShowBlueprintModal(true); }}
              className="bg-gradient-to-r from-[#E63946] to-[#F97316] hover:from-[#E63946]/90 hover:to-[#F97316]/90 text-white text-xs"
              data-testid="create-campaign-btn"
            >
              <Plus size={14} className="mr-1" />
              <span className="hidden sm:inline">New Campaign</span>
              <span className="sm:hidden">New</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="overflow-x-hidden">
        <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6 w-full">
          
          {/* Campaign Overview Stats */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={BarChart3} title="Campaign Overview" subtitle={`${currentYear} Performance Summary`} />
              
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20">
                        <Target size={20} className="text-[#E63946]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold font-mono text-white">{totalCampaigns}</p>
                        <p className="text-xs text-[#6B7280]">Total Campaigns {currentYear}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#8B5CF6]/20">
                        <Zap size={20} className="text-[#8B5CF6]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold font-mono text-white">{totalSubCampaigns}</p>
                        <p className="text-xs text-[#6B7280]">Sub Campaigns</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#10B981]/20">
                        <Play size={20} className="text-[#10B981]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold font-mono text-white">{runningCampaigns}</p>
                        <p className="text-xs text-[#6B7280]">Running Now</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#3B82F6]/20">
                        <CheckCircle2 size={20} className="text-[#3B82F6]" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold font-mono text-white">{completedCampaigns}</p>
                        <p className="text-xs text-[#6B7280]">Completed {currentYear}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Campaign Status Pipeline */}
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]">
                  <p className="text-xs text-[#6B7280] mb-3">Campaign Pipeline Status</p>
                  <div className="flex items-center gap-1 overflow-x-auto pb-1" style={{WebkitOverflowScrolling: 'touch'}}>
                    {Object.entries(statusConfig).filter(([key]) => key !== 'kiv').map(([key, config], idx, arr) => {
                      const count = campaigns.filter(c => c.status === key && new Date(c.startDate).getFullYear() === currentYear).length;
                      return (
                        <React.Fragment key={key}>
                          <div 
                            className="flex-shrink-0 w-20 sm:flex-1 sm:w-auto p-2 rounded-lg text-center cursor-pointer hover:scale-105 transition-transform"
                            style={{ backgroundColor: count > 0 ? `${config.color}20` : 'hsl(var(--surface))' }}
                            onClick={() => setFilterStatus(filterStatus === key ? 'all' : key)}
                          >
                            <div className="flex items-center justify-center gap-1 mb-1">
                              {React.createElement(config.icon, { size: 12, style: { color: config.color } })}
                              <span className="text-[9px] font-semibold" style={{ color: config.color }}>{config.label}</span>
                            </div>
                            <p className="text-lg font-bold font-mono" style={{ color: count > 0 ? config.color : '#6B7280' }}>{count}</p>
                          </div>
                          {idx < arr.length - 1 && (
                            <ChevronRight size={12} className="text-[#6B7280] flex-shrink-0" />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters & Year Navigation */}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-28 sm:w-40 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs">
                  <Filter size={14} className="mr-2 text-[#6B7280]" />
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                  <SelectItem value="all" className="text-white">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-28 sm:w-44 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white text-xs">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                  <SelectItem value="all" className="text-white">All Types</SelectItem>
                  {Object.entries(campaignTypes).map(([key, config]) => (
                    <SelectItem key={key} value={key} className="text-white">
                      <div className="flex items-center gap-2">
                        {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                        {config.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentYear(y => y - 1)}
                className="border-[hsl(var(--border))] text-white"
              >
                <ChevronLeft size={16} />
              </Button>
              <span className="text-lg font-bold text-white min-w-[80px] text-center">{currentYear}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentYear(y => y + 1)}
                className="border-[hsl(var(--border))] text-white"
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>

          {/* Timeline View */}
          {viewMode === 'timeline' && (
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#E63946]/10 via-transparent to-[#F97316]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader 
                  icon={GanttChart} 
                  title="Campaign Timeline" 
                  subtitle={
                    selectedTimelineWeek !== null 
                      ? `Week ${selectedTimelineWeek + 1}, ${fullMonthNames[selectedTimelineMonth]} ${currentYear} - Daily View`
                      : selectedTimelineMonth !== null 
                        ? `${fullMonthNames[selectedTimelineMonth]} ${currentYear} - Weekly View`
                        : selectedTimelineQuarter !== null
                          ? `Q${selectedTimelineQuarter + 1} ${currentYear} - Monthly View`
                          : `${currentYear} Annual Overview`
                  }
                  action={
                    (selectedTimelineQuarter !== null || selectedTimelineMonth !== null) ? (
                      <div className="flex items-center gap-2">
                        {/* Slide Navigation */}
                        {selectedTimelineMonth !== null && (
                          <div className="flex items-center gap-1 bg-[hsl(var(--surface))] rounded-lg p-0.5">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const quarterStart = selectedTimelineQuarter * 3;
                                const quarterEnd = quarterStart + 2;
                                if (selectedTimelineMonth > quarterStart) {
                                  setSelectedTimelineMonth(selectedTimelineMonth - 1);
                                  setSelectedTimelineWeek(null);
                                }
                              }}
                              className="h-6 w-6 p-0 text-white hover:bg-[#E63946]/20"
                              data-testid="prev-month-btn"
                            >
                              <ChevronLeft size={14} />
                            </Button>
                            <span className="text-xs font-medium text-white px-2 min-w-[80px] text-center">
                              {monthNames[selectedTimelineMonth]} {currentYear}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const quarterStart = selectedTimelineQuarter * 3;
                                const quarterEnd = quarterStart + 2;
                                if (selectedTimelineMonth < quarterEnd) {
                                  setSelectedTimelineMonth(selectedTimelineMonth + 1);
                                  setSelectedTimelineWeek(null);
                                }
                              }}
                              className="h-6 w-6 p-0 text-white hover:bg-[#E63946]/20"
                              data-testid="next-month-btn"
                            >
                              <ChevronRight size={14} />
                            </Button>
                          </div>
                        )}
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            if (selectedTimelineWeek !== null) {
                              setSelectedTimelineWeek(null);
                            } else if (selectedTimelineMonth !== null) {
                              setSelectedTimelineMonth(null);
                            } else {
                              setSelectedTimelineQuarter(null);
                            }
                          }}
                          className="h-7 border-[hsl(var(--border))] text-white"
                          data-testid="back-btn"
                        >
                          <ArrowLeft size={14} className="mr-1" />
                          {selectedTimelineWeek !== null 
                            ? 'Back to Month' 
                            : selectedTimelineMonth !== null 
                              ? 'Back to Quarter'
                              : 'Back to Year'}
                        </Button>
                      </div>
                    ) : null
                  }
                />
                
                <div className="p-4">
                  {/* Year View - Quarter Selection (Q1-Q4) */}
                  {selectedTimelineQuarter === null && selectedTimelineMonth === null ? (
                    <>
                      {/* Quarter Cards */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        {['Q1', 'Q2', 'Q3', 'Q4'].map((quarter, qIdx) => {
                          const quarterMonths = [qIdx * 3, qIdx * 3 + 1, qIdx * 3 + 2];
                          const quarterCampaignCount = yearCampaigns.filter(c => {
                            const startMonth = new Date(c.startDate).getMonth();
                            const endMonth = new Date(c.endDate).getMonth();
                            return quarterMonths.some(m => startMonth <= m && endMonth >= m);
                          }).length;
                          const quarterMonthNames = quarterMonths.map(m => monthNames[m]).join(' - ');
                          const isCurrentQuarter = currentYear === todayYear && quarterMonths.includes(todayMonth);
                          
                          return (
                            <div
                              key={quarter}
                              className={`p-4 rounded-xl border cursor-pointer hover:border-[#E63946]/50 hover:bg-[hsl(var(--surface))] transition-all group ${
                                isCurrentQuarter 
                                  ? 'bg-gradient-to-br from-[#E63946]/10 to-transparent border-[#E63946]/40 ring-1 ring-[#E63946]/20' 
                                  : 'border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]'
                              }`}
                              onClick={() => setSelectedTimelineQuarter(qIdx)}
                            >
                              <div className="text-center">
                                <h3 className="text-2xl font-bold text-[#E63946] group-hover:scale-110 transition-transform">
                                  {quarter}
                                  {isCurrentQuarter && <span className="ml-1 text-sm">●</span>}
                                </h3>
                                <p className="text-[10px] text-[#6B7280] mt-1">{quarterMonthNames}</p>
                                <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
                                  <p className="text-xl font-bold text-white">{quarterCampaignCount}</p>
                                  <p className="text-[10px] text-[#9CA3AF]">campaign{quarterCampaignCount !== 1 ? 's' : ''}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Campaign Bars - Year View */}
                      <div className="relative">
                        {/* Quarter separator lines */}
                        <div className="absolute top-0 bottom-0 left-[25%] w-px bg-[#E63946]/20 z-10" />
                        <div className="absolute top-0 bottom-0 left-[50%] w-px bg-[#E63946]/20 z-10" />
                        <div className="absolute top-0 bottom-0 left-[75%] w-px bg-[#E63946]/20 z-10" />
                        
                        {/* Current date indicator line */}
                        {currentYear === todayYear && (
                          <div 
                            className="absolute top-0 bottom-0 w-0.5 bg-[#E63946] z-20"
                            style={{ 
                              left: `${((todayMonth * 30 + todayDay) / 365) * 100}%`
                            }}
                          >
                            <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-[#E63946] rounded-full" />
                          </div>
                        )}
                      
                        <div className="space-y-3 min-h-[200px]">
                        {yearCampaigns.length === 0 ? (
                          <div className="text-center py-12">
                            <Target size={40} className="mx-auto text-[#6B7280] mb-3" />
                            <p className="text-sm text-[#6B7280]">No campaigns for {currentYear}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-3 border-[hsl(var(--border))] text-white"
                              onClick={() => {
                                setSelectedCampaign(null);
                                setShowBlueprintModal(true);
                              }}
                            >
                              <Plus size={14} className="mr-1" />
                              Create Campaign
                            </Button>
                          </div>
                        ) : (
                          yearCampaigns.map((campaign) => {
                            const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
                            const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                            const pos = getCampaignPosition(campaign);
                            
                            return (
                              <div key={campaign.id} className="relative h-14 group/campaign">
                                {/* Campaign Bar - COLOR BY STATUS */}
                                <div
                                  className="absolute h-12 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg overflow-hidden border border-[hsl(var(--border))]"
                                  style={{
                                    left: pos.left,
                                    width: pos.width,
                                    minWidth: '120px',
                                    backgroundColor: `${statusConf.color}20`,
                                    borderLeft: `4px solid ${statusConf.color}`,
                                  }}
                                  onClick={() => {
                                    // Direct to Campaign Overview (skip popup)
                                    setSelectedCampaign(campaign);
                                    setBlueprintDefaultView('document');
                                    setShowBlueprintModal(true);
                                  }}
                                  data-testid={`campaign-bar-${campaign.id}`}
                                >
                                  {/* Content */}
                                  <div className="relative h-full flex items-center px-3 gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-white truncate">{campaign.name}</p>
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          className="text-[8px] px-1.5 py-0"
                                          style={{ backgroundColor: `${statusConf.color}40`, color: statusConf.color }}
                                        >
                                          {statusConf.label}
                                        </Badge>
                                        {campaign.subCampaigns?.length > 0 && (
                                          <span className="text-[8px] text-[#8B5CF6]">+{campaign.subCampaigns.length} sub</span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Quick Edit - opens Blueprint */}
                                    <div className="flex items-center gap-1 opacity-0 group-hover/campaign:opacity-100 transition-opacity">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 text-white hover:bg-white/20"
                                        onClick={(e) => { 
                                          e.stopPropagation(); 
                                          setSelectedCampaign(campaign);
                                          setBlueprintDefaultView('edit');
                                          setShowBlueprintModal(true);
                                        }}
                                      >
                                        <Edit2 size={12} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        )}
                        </div>
                      </div>
                    </>
                  ) : selectedTimelineMonth === null ? (
                    <>
                      {/* Quarter Drill-down - Month View */}
                      <div className="flex border-b border-[hsl(var(--border))] pb-2 mb-4">
                        {[0, 1, 2].map((monthOffset) => {
                          const monthIdx = selectedTimelineQuarter * 3 + monthOffset;
                          const monthCampaignCount = yearCampaigns.filter(c => {
                            const startMonth = new Date(c.startDate).getMonth();
                            const endMonth = new Date(c.endDate).getMonth();
                            return startMonth <= monthIdx && endMonth >= monthIdx;
                          }).length;
                          
                          return (
                            <div 
                              key={monthIdx}
                              className="flex-1 text-center cursor-pointer hover:bg-[hsl(var(--surface-elevated))] rounded py-2 transition-colors mx-1"
                              onClick={() => setSelectedTimelineMonth(monthIdx)}
                            >
                              <p className="text-lg font-bold text-white">{fullMonthNames[monthIdx]}</p>
                              <p className="text-xl font-bold text-[#E63946] mt-1">{monthCampaignCount}</p>
                              <p className="text-[10px] text-[#9CA3AF]">campaign{monthCampaignCount !== 1 ? 's' : ''}</p>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Campaign Bars - Quarter View */}
                      <div className="space-y-3 min-h-[250px]">
                        {(() => {
                          const quarterMonths = [selectedTimelineQuarter * 3, selectedTimelineQuarter * 3 + 1, selectedTimelineQuarter * 3 + 2];
                          const quarterCampaigns = yearCampaigns.filter(c => {
                            const startMonth = new Date(c.startDate).getMonth();
                            const endMonth = new Date(c.endDate).getMonth();
                            return quarterMonths.some(m => startMonth <= m && endMonth >= m);
                          });
                          
                          if (quarterCampaigns.length === 0) {
                            return (
                              <div className="text-center py-12">
                                <CalendarIcon size={40} className="mx-auto text-[#6B7280] mb-3" />
                                <p className="text-sm text-[#6B7280]">No campaigns in Q{selectedTimelineQuarter + 1} {currentYear}</p>
                              </div>
                            );
                          }
                          
                          // Calculate position within quarter (3 months)
                          const quarterStart = new Date(currentYear, selectedTimelineQuarter * 3, 1);
                          const quarterEnd = new Date(currentYear, selectedTimelineQuarter * 3 + 3, 0);
                          const totalDays = Math.ceil((quarterEnd - quarterStart) / (1000 * 60 * 60 * 24));
                          
                          return quarterCampaigns.map((campaign) => {
                            const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                            const startDate = new Date(campaign.startDate);
                            const endDate = new Date(campaign.endDate);
                            
                            const effectiveStart = startDate < quarterStart ? quarterStart : startDate;
                            const effectiveEnd = endDate > quarterEnd ? quarterEnd : endDate;
                            
                            const startOffset = Math.ceil((effectiveStart - quarterStart) / (1000 * 60 * 60 * 24));
                            const duration = Math.ceil((effectiveEnd - effectiveStart) / (1000 * 60 * 60 * 24)) + 1;
                            
                            const leftPercent = (startOffset / totalDays) * 100;
                            const widthPercent = (duration / totalDays) * 100;
                            
                            return (
                              <div key={campaign.id} className="relative h-14 group/campaign">
                                <div
                                  className="absolute h-12 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg overflow-hidden border border-[hsl(var(--border))]"
                                  style={{
                                    left: `${leftPercent}%`,
                                    width: `${Math.max(widthPercent, 10)}%`,
                                    minWidth: '120px',
                                    backgroundColor: `${statusConf.color}20`,
                                    borderLeft: `4px solid ${statusConf.color}`,
                                  }}
                                  onClick={() => {
                                    setSelectedCampaign(campaign);
                                    setBlueprintDefaultView('document');
                                    setShowBlueprintModal(true);
                                  }}
                                >
                                  <div className="h-full flex items-center px-3">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-white truncate">{campaign.name}</p>
                                      <Badge
                                        className="text-[8px] px-1.5 py-0 mt-1"
                                        style={{ backgroundColor: `${statusConf.color}40`, color: statusConf.color }}
                                      >
                                        {statusConf.label}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </>
                  ) : selectedTimelineWeek === null ? (
                    <>
                      {/* Monthly Drill-down - Week View */}
                      <div className="flex border-b border-[hsl(var(--border))] pb-2 mb-4">
                        {['Week 1', 'Week 2', 'Week 3', 'Week 4', 'Week 5'].map((week, idx) => {
                          const weekStart = idx * 7 + 1;
                          const daysInMonth = new Date(currentYear, selectedTimelineMonth + 1, 0).getDate();
                          const weekEnd = Math.min((idx + 1) * 7, daysInMonth);
                          
                          // Skip week 5 if month doesn't have enough days
                          if (weekStart > daysInMonth) return null;
                          
                          // Count campaigns in this week
                          const weekCampaignCount = yearCampaigns.filter(c => {
                            const startDate = new Date(c.startDate);
                            const endDate = new Date(c.endDate);
                            const weekStartDate = new Date(currentYear, selectedTimelineMonth, weekStart);
                            const weekEndDate = new Date(currentYear, selectedTimelineMonth, weekEnd);
                            return startDate <= weekEndDate && endDate >= weekStartDate;
                          }).length;
                          
                          return (
                            <div 
                              key={week} 
                              className="flex-1 text-center cursor-pointer hover:bg-[hsl(var(--surface-elevated))] rounded py-1 transition-colors"
                              onClick={() => setSelectedTimelineWeek(idx)}
                            >
                              <p className="text-xs font-semibold text-[#9CA3AF]">{week}</p>
                              <p className="text-[9px] text-[#6B7280]">{weekStart}-{weekEnd} {monthNames[selectedTimelineMonth]}</p>
                              {weekCampaignCount > 0 && (
                                <p className="text-[8px] text-[#E63946] mt-0.5">{weekCampaignCount} active</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Campaign Bars - Week View */}
                      <div className="space-y-3 min-h-[300px]">
                        {(() => {
                          const monthCampaigns = yearCampaigns.filter(c => {
                            const startDate = new Date(c.startDate);
                            const endDate = new Date(c.endDate);
                            const monthStart = new Date(currentYear, selectedTimelineMonth, 1);
                            const monthEnd = new Date(currentYear, selectedTimelineMonth + 1, 0);
                            return startDate <= monthEnd && endDate >= monthStart;
                          });
                          
                          if (monthCampaigns.length === 0) {
                            return (
                              <div className="text-center py-12">
                                <CalendarIcon size={40} className="mx-auto text-[#6B7280] mb-3" />
                                <p className="text-sm text-[#6B7280]">No campaigns in {fullMonthNames[selectedTimelineMonth]} {currentYear}</p>
                              </div>
                            );
                          }
                          
                          const daysInMonth = new Date(currentYear, selectedTimelineMonth + 1, 0).getDate();
                          
                          return monthCampaigns.map((campaign) => {
                            const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                            const startDate = new Date(campaign.startDate);
                            const endDate = new Date(campaign.endDate);
                            
                            const monthStart = new Date(currentYear, selectedTimelineMonth, 1);
                            const monthEnd = new Date(currentYear, selectedTimelineMonth + 1, 0);
                            
                            const effectiveStart = startDate < monthStart ? 1 : startDate.getDate();
                            const effectiveEnd = endDate > monthEnd ? daysInMonth : endDate.getDate();
                            
                            const leftPercent = ((effectiveStart - 1) / daysInMonth) * 100;
                            const widthPercent = ((effectiveEnd - effectiveStart + 1) / daysInMonth) * 100;
                            
                            return (
                              <div key={campaign.id} className="relative h-14 group/campaign">
                                <div
                                  className="absolute h-12 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg overflow-hidden border border-[hsl(var(--border))]"
                                  style={{
                                    left: `${leftPercent}%`,
                                    width: `${Math.max(widthPercent, 15)}%`,
                                    minWidth: '100px',
                                    backgroundColor: `${statusConf.color}20`,
                                    borderLeft: `4px solid ${statusConf.color}`,
                                  }}
                                  onClick={() => {
                                    setSelectedCampaign(campaign);
                                    setBlueprintDefaultView('document');
                                    setShowBlueprintModal(true);
                                  }}
                                  data-testid={`campaign-bar-weekly-${campaign.id}`}
                                >
                                  <div className="h-full flex items-center px-3 gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-semibold text-white truncate">{campaign.name}</p>
                                      <div className="flex items-center gap-2">
                                        <Badge
                                          className="text-[8px] px-1.5 py-0"
                                          style={{ backgroundColor: `${statusConf.color}40`, color: statusConf.color }}
                                        >
                                          {statusConf.label}
                                        </Badge>
                                        <span className="text-[8px] text-[#6B7280]">
                                          {effectiveStart}-{effectiveEnd} {monthNames[selectedTimelineMonth]}
                                        </span>
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-1 opacity-0 group-hover/campaign:opacity-100 transition-opacity">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 text-white hover:bg-white/20"
                                        onClick={(e) => { 
                                          e.stopPropagation(); 
                                          setSelectedCampaign(campaign);
                                          setBlueprintDefaultView('edit');
                                          setShowBlueprintModal(true);
                                        }}
                                      >
                                        <Edit2 size={12} />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      
                      {/* Tip for weekly view */}
                      <p className="text-[10px] text-[#6B7280] mt-2 text-center">
                        💡 Tip: Click on any week to see daily breakdown
                      </p>
                    </>
                  ) : (
                    <>
                      {/* Weekly Drill-down - Daily View */}
                      {(() => {
                        const daysInMonth = new Date(currentYear, selectedTimelineMonth + 1, 0).getDate();
                        const weekStart = selectedTimelineWeek * 7 + 1;
                        const weekEnd = Math.min((selectedTimelineWeek + 1) * 7, daysInMonth);
                        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                        
                        return (
                          <>
                            <div className="flex border-b border-[hsl(var(--border))] pb-2 mb-4">
                              {Array.from({ length: weekEnd - weekStart + 1 }).map((_, idx) => {
                                const dayNum = weekStart + idx;
                                const date = new Date(currentYear, selectedTimelineMonth, dayNum);
                                const dayName = dayNames[date.getDay()];
                                const isToday = new Date().toDateString() === date.toDateString();
                                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                                
                                // Count campaigns on this day
                                const dayCampaignCount = yearCampaigns.filter(c => {
                                  const startDate = new Date(c.startDate);
                                  const endDate = new Date(c.endDate);
                                  return startDate <= date && endDate >= date;
                                }).length;
                                
                                return (
                                  <div 
                                    key={dayNum} 
                                    className={`flex-1 text-center py-2 rounded transition-colors ${isToday ? 'bg-[#E63946]/20 ring-1 ring-[#E63946]' : ''} ${isWeekend ? 'bg-[hsl(var(--surface-elevated))]' : ''}`}
                                  >
                                    <p className={`text-[10px] font-semibold ${isWeekend ? 'text-[#6B7280]' : 'text-[#9CA3AF]'}`}>{dayName}</p>
                                    <p className={`text-lg font-bold font-mono ${isToday ? 'text-[#E63946]' : 'text-white'}`}>{dayNum}</p>
                                    {dayCampaignCount > 0 && (
                                      <div className="flex justify-center gap-0.5 mt-1">
                                        {Array.from({ length: Math.min(dayCampaignCount, 3) }).map((_, i) => (
                                          <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#E63946]" />
                                        ))}
                                        {dayCampaignCount > 3 && (
                                          <span className="text-[8px] text-[#E63946]">+{dayCampaignCount - 3}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            
                            {/* Campaign Bars - Daily View */}
                            <div className="space-y-3 min-h-[300px]">
                              {(() => {
                                const weekStartDate = new Date(currentYear, selectedTimelineMonth, weekStart);
                                const weekEndDate = new Date(currentYear, selectedTimelineMonth, weekEnd);
                                
                                const weekCampaigns = yearCampaigns.filter(c => {
                                  const startDate = new Date(c.startDate);
                                  const endDate = new Date(c.endDate);
                                  return startDate <= weekEndDate && endDate >= weekStartDate;
                                });
                                
                                if (weekCampaigns.length === 0) {
                                  return (
                                    <div className="text-center py-12">
                                      <CalendarIcon size={40} className="mx-auto text-[#6B7280] mb-3" />
                                      <p className="text-sm text-[#6B7280]">No campaigns in Week {selectedTimelineWeek + 1}</p>
                                    </div>
                                  );
                                }
                                
                                const totalDays = weekEnd - weekStart + 1;
                                
                                return weekCampaigns.map((campaign) => {
                                  const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                                  const startDate = new Date(campaign.startDate);
                                  const endDate = new Date(campaign.endDate);
                                  
                                  const effectiveStart = startDate < weekStartDate ? weekStart : startDate.getDate();
                                  const effectiveEnd = endDate > weekEndDate ? weekEnd : endDate.getDate();
                                  
                                  const leftPercent = ((effectiveStart - weekStart) / totalDays) * 100;
                                  const widthPercent = ((effectiveEnd - effectiveStart + 1) / totalDays) * 100;
                                  
                                  return (
                                    <div key={campaign.id} className="relative h-16 group/campaign">
                                      <div
                                        className="absolute h-14 rounded-lg cursor-pointer transition-all hover:scale-[1.02] hover:shadow-lg overflow-hidden border border-[hsl(var(--border))]"
                                        style={{
                                          left: `${leftPercent}%`,
                                          width: `${Math.max(widthPercent, 14)}%`,
                                          minWidth: '80px',
                                          backgroundColor: `${statusConf.color}20`,
                                          borderLeft: `4px solid ${statusConf.color}`,
                                        }}
                                        onClick={() => {
                                          setSelectedCampaign(campaign);
                                          setBlueprintDefaultView('document');
                                          setShowBlueprintModal(true);
                                        }}
                                        data-testid={`campaign-bar-daily-${campaign.id}`}
                                      >
                                        <div className="h-full flex items-center px-3 gap-2">
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold text-white truncate">{campaign.name}</p>
                                            <div className="flex items-center gap-1 mt-0.5">
                                              <Badge
                                                className="text-[8px] px-1.5 py-0"
                                                style={{ backgroundColor: `${statusConf.color}40`, color: statusConf.color }}
                                              >
                                                {statusConf.label}
                                              </Badge>
                                            </div>
                                            <p className="text-[9px] text-[#6B7280] mt-0.5">
                                              Day {effectiveStart}-{effectiveEnd}
                                            </p>
                                          </div>
                                          
                                          <div className="flex items-center gap-1 opacity-0 group-hover/campaign:opacity-100 transition-opacity">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 w-6 p-0 text-white hover:bg-white/20"
                                              onClick={(e) => { 
                                                e.stopPropagation(); 
                                                setSelectedCampaign(campaign);
                                                setBlueprintDefaultView('edit');
                                                setShowBlueprintModal(true);
                                              }}
                                            >
                                              <Edit2 size={12} />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                });
                              })()}
                            </div>
                          </>
                        );
                      })()}
                    </>
                  )}
                  
                  {/* Legend - Status Colors */}
                  <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[hsl(var(--border))]">
                    <span className="text-xs text-[#6B7280] font-semibold">Status Colors:</span>
                    {Object.entries(statusConfig).filter(([key]) => !['kiv'].includes(key)).slice(0, 8).map(([key, config]) => (
                      <div key={key} className="flex items-center gap-1.5">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: config.color }} />
                        <span className="text-[10px] text-[#9CA3AF]">{config.label}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Tip */}
                  {selectedTimelineMonth === null && selectedTimelineQuarter === null && (
                    <p className="text-[10px] text-[#6B7280] mt-2 text-center">
                      💡 Tip: Click on any quarter (Q1-Q4) to drill-down
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <div className="relative group/section">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#3B82F6]/10 via-transparent to-[#10B981]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
              
              <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                <SectionHeader 
                  icon={CalendarIcon} 
                  title="Campaign Calendar" 
                  subtitle={`${fullMonthNames[currentMonth]} ${currentYear}`}
                  action={
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (currentMonth === 0) {
                            setCurrentMonth(11);
                            setCurrentYear(y => y - 1);
                          } else {
                            setCurrentMonth(m => m - 1);
                          }
                        }}
                        className="h-7 w-7 p-0 border-[hsl(var(--border))] text-white"
                      >
                        <ChevronLeft size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (currentMonth === 11) {
                            setCurrentMonth(0);
                            setCurrentYear(y => y + 1);
                          } else {
                            setCurrentMonth(m => m + 1);
                          }
                        }}
                        className="h-7 w-7 p-0 border-[hsl(var(--border))] text-white"
                      >
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  }
                />
                
                <div className="p-4">
                  {/* Campaigns for current month */}
                  <div className="space-y-3">
                    {(() => {
                      const monthCampaigns = filteredCampaigns.filter(c => {
                        const startDate = new Date(c.startDate);
                        const endDate = new Date(c.endDate);
                        return (
                          (startDate.getMonth() === currentMonth && startDate.getFullYear() === currentYear) ||
                          (endDate.getMonth() === currentMonth && endDate.getFullYear() === currentYear) ||
                          (startDate <= new Date(currentYear, currentMonth, 1) && endDate >= new Date(currentYear, currentMonth + 1, 0))
                        );
                      });
                      
                      if (monthCampaigns.length === 0) {
                        return (
                          <div className="text-center py-12">
                            <CalendarIcon size={40} className="mx-auto text-[#6B7280] mb-3" />
                            <p className="text-sm text-[#6B7280]">No campaigns in {fullMonthNames[currentMonth]} {currentYear}</p>
                          </div>
                        );
                      }
                      
                      return monthCampaigns.map((campaign) => {
                        const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
                        const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                        const prep = getPreparationProgress(campaign.preparation);
                        
                        return (
                          <div
                            key={campaign.id}
                            className="p-4 rounded-xl border bg-[hsl(var(--surface-elevated))] cursor-pointer hover:border-[#E63946]/50 transition-all"
                            style={{ borderColor: `${typeConfig.color}30` }}
                            onClick={() => openDetailModal(campaign)}
                            data-testid={`campaign-card-${campaign.id}`}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div
                                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                                  style={{ backgroundColor: `${typeConfig.color}20` }}
                                >
                                  {React.createElement(typeConfig.icon, { size: 20, style: { color: typeConfig.color } })}
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-white">{campaign.name}</p>
                                  <p className="text-xs text-[#6B7280]">
                                    {new Date(campaign.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(campaign.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                  </p>
                                </div>
                              </div>
                              <Badge
                                className="text-[10px]"
                                style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}
                              >
                                {React.createElement(statusConf.icon, { size: 10, className: 'mr-1' })}
                                {statusConf.label}
                              </Badge>
                            </div>
                            
                            {/* Preparation Progress */}
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-[#6B7280]">Preparation</span>
                                <span className="text-[10px] text-white">{prep.completed}/{prep.total}</span>
                              </div>
                              <div className="h-1.5 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{ 
                                    width: `${prep.percentage}%`,
                                    backgroundColor: prep.percentage === 100 ? '#10B981' : '#F59E0B'
                                  }}
                                />
                              </div>
                            </div>
                            
                            {/* Metrics Preview */}
                            {campaign.metrics?.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {campaign.metrics.slice(0, 3).map((metric, idx) => (
                                  <div key={idx} className="bg-[hsl(var(--surface))] px-2 py-1 rounded text-[10px]">
                                    <span className="text-[#6B7280]">{metric.label}: </span>
                                    <span className="text-white font-semibold">{metric.value}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {/* Sub Campaigns */}
                            {campaign.subCampaigns?.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-[hsl(var(--border))]">
                                <p className="text-[10px] text-[#8B5CF6] mb-1">+{campaign.subCampaigns.length} Sub Campaign(s)</p>
                              </div>
                            )}
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Campaign List */}
          <div className="relative group/section">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#8B5CF6]/10 via-transparent to-[#3B82F6]/10 rounded-2xl blur-xl opacity-0 group-hover/section:opacity-100 transition-opacity duration-500" />
            
            <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
              <SectionHeader icon={LayoutGrid} title="All Campaigns" subtitle={`${yearCampaigns.length} campaigns in ${currentYear}`} />
              
              <div className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  {yearCampaigns.map((campaign) => {
                    const typeConfig = campaignTypes[campaign.type] || campaignTypes.other;
                    const statusConf = statusConfig[campaign.status] || statusConfig.planning;
                    const prep = getPreparationProgress(campaign.preparation);
                    
                    return (
                      <div
                        key={campaign.id}
                        className="p-4 rounded-xl border bg-[hsl(var(--surface-elevated))] cursor-pointer hover:border-[#E63946]/50 transition-all group/card"
                        style={{ borderColor: `${typeConfig.color}30` }}
                        onClick={() => openDetailModal(campaign)}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${typeConfig.color}20` }}
                          >
                            {React.createElement(typeConfig.icon, { size: 16, style: { color: typeConfig.color } })}
                          </div>
                          <div className="flex items-center gap-1">
                            <Badge
                              className="text-[9px] px-1.5"
                              style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}
                            >
                              {statusConf.label}
                            </Badge>
                            {campaign.status !== 'completed' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 opacity-0 group-hover/card:opacity-100 text-[#3B82F6]"
                                onClick={(e) => { e.stopPropagation(); openEditModal(campaign); }}
                              >
                                <Edit2 size={12} />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        <p className="text-sm font-semibold text-white mb-1 truncate">{campaign.name}</p>
                        <p className="text-[10px] text-[#6B7280] mb-2">
                          {new Date(campaign.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(campaign.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        
                        {/* Preparation Bar */}
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-1 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{ 
                                width: `${prep.percentage}%`,
                                backgroundColor: prep.percentage === 100 ? '#10B981' : '#F59E0B'
                              }}
                            />
                          </div>
                          <span className="text-[9px] text-[#6B7280]">{prep.percentage}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Create Campaign Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus size={20} className="text-[#E63946]" />
              Create New Campaign
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-[#9CA3AF] text-xs">Campaign Name *</Label>
              <Input
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                placeholder="e.g., March Trading Challenge"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"
                data-testid="create-campaign-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Type *</Label>
                <Select value={newCampaign.type} onValueChange={(v) => setNewCampaign({ ...newCampaign, type: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    {Object.entries(campaignTypes).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        <div className="flex items-center gap-2">
                          {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">Status</Label>
                <Select value={newCampaign.status} onValueChange={(v) => setNewCampaign({ ...newCampaign, status: v })}>
                  <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="text-white">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Start Date *</Label>
                <Input
                  type="date"
                  value={newCampaign.startDate}
                  onChange={(e) => setNewCampaign({ ...newCampaign, startDate: e.target.value })}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  data-testid="create-campaign-start"
                />
              </div>
              <div>
                <Label className="text-[#9CA3AF] text-xs">End Date *</Label>
                <Input
                  type="date"
                  value={newCampaign.endDate}
                  onChange={(e) => setNewCampaign({ ...newCampaign, endDate: e.target.value })}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  data-testid="create-campaign-end"
                />
              </div>
            </div>
            
            <div>
              <Label className="text-[#9CA3AF] text-xs">Description</Label>
              <Textarea
                value={newCampaign.description}
                onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })}
                placeholder="Campaign description..."
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px]"
              />
            </div>
            
            {/* Preparation Checklist */}
            <div>
              <Label className="text-[#9CA3AF] text-xs mb-2 block">Preparation Checklist</Label>
              <div className="grid grid-cols-2 gap-2">
                {preparationChecklist.map((item) => (
                  <div key={item.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`prep-${item.id}`}
                      checked={newCampaign.preparation[item.id]}
                      onCheckedChange={(checked) => setNewCampaign({
                        ...newCampaign,
                        preparation: { ...newCampaign.preparation, [item.id]: checked }
                      })}
                      className="border-[hsl(var(--border))]"
                    />
                    <label htmlFor={`prep-${item.id}`} className="text-xs text-white cursor-pointer">
                      {item.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-[hsl(var(--border))] text-white">
              Cancel
            </Button>
            <Button
              onClick={handleCreateCampaign}
              className="bg-gradient-to-r from-[#E63946] to-[#F97316] text-white"
              disabled={!newCampaign.name || !newCampaign.startDate || !newCampaign.endDate}
              data-testid="create-campaign-submit"
            >
              Create Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedCampaign && (() => {
            const typeConfig = campaignTypes[selectedCampaign.type] || campaignTypes.other;
            const statusConf = statusConfig[selectedCampaign.status] || statusConfig.planning;
            const prep = getPreparationProgress(selectedCampaign.preparation);
            
            return (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${typeConfig.color}20` }}
                      >
                        {React.createElement(typeConfig.icon, { size: 24, style: { color: typeConfig.color } })}
                      </div>
                      <div>
                        <DialogTitle className="text-white text-lg">{selectedCampaign.name}</DialogTitle>
                        <p className="text-xs text-[#6B7280]">{typeConfig.label}</p>
                      </div>
                    </div>
                    <Badge
                      className="text-xs"
                      style={{ backgroundColor: `${statusConf.color}20`, color: statusConf.color }}
                    >
                      {React.createElement(statusConf.icon, { size: 12, className: 'mr-1' })}
                      {statusConf.label}
                    </Badge>
                  </div>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                  {/* Date & Description */}
                  <div>
                    <p className="text-sm text-white mb-2">
                      <CalendarIcon size={14} className="inline mr-2 text-[#6B7280]" />
                      {new Date(selectedCampaign.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} - {new Date(selectedCampaign.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                    {selectedCampaign.description && (
                      <p className="text-sm text-[#9CA3AF]">{selectedCampaign.description}</p>
                    )}
                  </div>
                  
                  {/* Preparation Progress */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-semibold text-white">Preparation Progress</h4>
                      <span className="text-xs text-[#6B7280]">{prep.completed}/{prep.total} ({prep.percentage}%)</span>
                    </div>
                    <div className="h-2 bg-[hsl(var(--surface-elevated))] rounded-full overflow-hidden mb-3">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${prep.percentage}%`,
                          backgroundColor: prep.percentage === 100 ? '#10B981' : '#F59E0B'
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {preparationChecklist.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                            selectedCampaign.preparation[item.id]
                              ? 'bg-[#10B981]/20 text-[#10B981]'
                              : 'bg-[hsl(var(--surface-elevated))] text-[#6B7280]'
                          }`}
                        >
                          {selectedCampaign.preparation[item.id] ? (
                            <Check size={12} />
                          ) : (
                            <X size={12} />
                          )}
                          {item.label}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Performance Metrics */}
                  {selectedCampaign.metrics?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-3">Performance Metrics</h4>
                      <div className="grid grid-cols-3 gap-3">
                        {selectedCampaign.metrics.map((metric, idx) => (
                          <div key={idx} className="bg-[hsl(var(--surface-elevated))] p-3 rounded-lg">
                            <p className="text-[10px] text-[#6B7280] mb-1">{metric.label}</p>
                            <p className="text-lg font-bold text-white">{metric.value}</p>
                            {metric.target && (
                              <p className="text-[10px] text-[#6B7280]">Target: {metric.target}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Sub Campaigns */}
                  {selectedCampaign.subCampaigns?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-white mb-3">Sub Campaigns</h4>
                      <div className="space-y-2">
                        {selectedCampaign.subCampaigns.map((sub) => {
                          const subStatus = statusConfig[sub.status];
                          return (
                            <div key={sub.id} className="flex items-center justify-between p-3 bg-[hsl(var(--surface-elevated))] rounded-lg">
                              <div>
                                <p className="text-sm text-white">{sub.name}</p>
                                <p className="text-[10px] text-[#6B7280]">
                                  {new Date(sub.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(sub.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              </div>
                              <Badge
                                className="text-[9px]"
                                style={{ backgroundColor: `${subStatus.color}20`, color: subStatus.color }}
                              >
                                {subStatus.label}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setBlueprintDefaultView('document');
                      setShowDetailModal(false);
                      setShowBlueprintModal(true);
                    }}
                    className="border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/20 w-full sm:w-auto"
                    data-testid="campaign-overview-btn"
                  >
                    <Eye size={14} className="mr-1" />
                    CAMPAIGN OVERVIEW
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setBlueprintDefaultView('edit');
                      setShowDetailModal(false);
                      setShowBlueprintModal(true);
                    }}
                    className="border-[#F97316] text-[#F97316] hover:bg-[#F97316]/20 w-full sm:w-auto"
                    data-testid="view-blueprint-btn"
                  >
                    <Layers size={14} className="mr-1" />
                    View Blueprint
                  </Button>
                  {selectedCampaign.status !== 'completed' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => handleDeleteCampaign(selectedCampaign.id)}
                        className="border-[#E63946] text-[#E63946] hover:bg-[#E63946]/20"
                        data-testid="delete-campaign-btn"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                      <Button
                        onClick={() => openEditModal(selectedCampaign)}
                        className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white"
                        data-testid="edit-campaign-btn"
                      >
                        <Edit2 size={14} className="mr-1" />
                        Edit Campaign
                      </Button>
                    </>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit Campaign Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit2 size={20} className="text-[#3B82F6]" />
              Edit Campaign
            </DialogTitle>
          </DialogHeader>
          
          {editingCampaign && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[#9CA3AF] text-xs">Campaign Name *</Label>
                <Input
                  value={editingCampaign.name}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, name: e.target.value })}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Type</Label>
                  <Select value={editingCampaign.type} onValueChange={(v) => setEditingCampaign({ ...editingCampaign, type: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      {Object.entries(campaignTypes).map(([key, config]) => (
                        <SelectItem key={key} value={key} className="text-white">
                          <div className="flex items-center gap-2">
                            {React.createElement(config.icon, { size: 14, style: { color: config.color } })}
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Status</Label>
                  <Select value={editingCampaign.status} onValueChange={(v) => setEditingCampaign({ ...editingCampaign, status: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                      {Object.entries(statusConfig).map(([key, config]) => (
                        <SelectItem key={key} value={key} className="text-white">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config.color }} />
                            {config.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#9CA3AF] text-xs">Start Date</Label>
                  <Input
                    type="date"
                    value={editingCampaign.startDate}
                    onChange={(e) => setEditingCampaign({ ...editingCampaign, startDate: e.target.value })}
                    className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  />
                </div>
                <div>
                  <Label className="text-[#9CA3AF] text-xs">End Date</Label>
                  <Input
                    type="date"
                    value={editingCampaign.endDate}
                    onChange={(e) => setEditingCampaign({ ...editingCampaign, endDate: e.target.value })}
                    className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 [color-scheme:dark]"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-[#9CA3AF] text-xs">Description</Label>
                <Textarea
                  value={editingCampaign.description}
                  onChange={(e) => setEditingCampaign({ ...editingCampaign, description: e.target.value })}
                  className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white mt-1 min-h-[60px]"
                />
              </div>
              
              {/* Preparation Checklist */}
              <div>
                <Label className="text-[#9CA3AF] text-xs mb-2 block">Preparation Checklist</Label>
                <div className="grid grid-cols-2 gap-2">
                  {preparationChecklist.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-prep-${item.id}`}
                        checked={editingCampaign.preparation[item.id]}
                        onCheckedChange={(checked) => setEditingCampaign({
                          ...editingCampaign,
                          preparation: { ...editingCampaign.preparation, [item.id]: checked }
                        })}
                        className="border-[hsl(var(--border))]"
                      />
                      <label htmlFor={`edit-prep-${item.id}`} className="text-xs text-white cursor-pointer">
                        {item.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)} className="border-[hsl(var(--border))] text-white">
              Cancel
            </Button>
            <Button
              onClick={handleEditCampaign}
              className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Campaign Blueprint Modal - UNIFIED CREATE & EDIT */}
      <CampaignBlueprintWizard
        open={showBlueprintModal}
        onOpenChange={(open) => {
          setShowBlueprintModal(open);
          if (!open) setBlueprintDefaultView('edit'); // Reset on close
        }}
        campaign={selectedCampaign}
        onSave={(campaignData) => {
          let newList;
          if (selectedCampaign) {
            newList = campaigns.map(c => c.id === selectedCampaign.id ? { ...c, ...campaignData } : c);
          } else {
            newList = [...campaigns, campaignData];
          }
          setCampaigns(newList);
          saveCampaigns(newList);
          setSelectedCampaign(null);
          setBlueprintDefaultView('edit');
        }}
        mode={selectedCampaign ? 'edit' : 'create'}
        defaultViewMode={blueprintDefaultView}
      />
    </>
  );
}
